import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { AnimatePresence } from 'framer-motion';

import { SortBy } from '@/types/api';
import useStore from '@/store';

import Text from '@/components/Text';
import Icon from '@/components/Icon';
import Spinner from '@/components/Spinner';

import ListEntry from './ListEntry';
import SortButton from '../SortButton';
import ListEntrySkeleton from './ListEntry.skeleton';

import {
  ListHeaderDesktop,
  ListWrapper,
  ListLoader,
  LoadMoreButton,
  NoResultsWrapper
} from './List.style';

const List = () => {
  const router = useRouter();
  const { t } = useTranslation('common');

  const results = useStore(state => state.search.results);
  const fetchMore = useStore(state => state.search.fetchMore);
  const loadingMore = useStore(state => state.search.loadingMore);
  const loading = useStore(state => state.search.loading);
  const limit = useStore(state => state.search.limit);
  const count = useStore(state => state.search.count);
  const updateSortBy = useStore(state => state.search.updateSortBy);
  const updateSortOrder = useStore(state => state.search.updateSortOrder);
  const sortBy = useStore(state => state.search.sortBy);
  const sortOrder = useStore(state => state.search.sortOrder);

  const updateSelected = useStore(state => state.general.updateSelected);

  const current = results.length;

  const sortByKeys: { id: SortBy; label: string }[] = [
    { id: 'name', label: t('biography.name') },
    { id: 'birthDate', label: t('biography.birthDate') },
    { id: 'deathDate', label: t('biography.deathDate') },
    { id: 'deportedTo', label: t('biography.deportedTo') },
    { id: 'deportationDate', label: t('biography.deportationDate') }
  ];

  const skeletons = useMemo(() => {
    const skeletonCount = count > limit ? limit : count;
    return Array.from(Array(skeletonCount === 0 ? limit : skeletonCount).keys());
  }, [limit, count]);

  const isLoading = loading || loadingMore;

  if (!loading && results.length === 0) {
    return (
      <NoResultsWrapper>
        <Text>{t('form.labels.noResults')}</Text>
      </NoResultsWrapper>
    );
  }

  return (
    <>
      <ListWrapper>
        <ListHeaderDesktop>
          {sortByKeys.map(i => (
            <SortButton
              key={i.id}
              id={i.id}
              label={i.label}
              ariaLabel={t('accessibility.sortBy', { label: i.label })}
            />
          ))}
        </ListHeaderDesktop>

        <ol role="feed" aria-busy={loading} aria-live="polite" aria-atomic="true">
          {!loading &&
            results.map(i => (
              <li key={i.slug}>
                <ListEntry
                  onClick={() => updateSelected(i.slug, router.locale)}
                  name={i.name}
                  deathDate={i.deathDate}
                  birthDate={i.birthDate}
                  deportationDate={i.deportationDate}
                  deportedTo={i.deportedTo}
                />
              </li>
            ))}

          {isLoading ? skeletons.map(i => <ListEntrySkeleton key={i} />) : null}
        </ol>

        {current < count && (
          <LoadMoreButton type="button" onClick={fetchMore}>
            <Icon icon="Expand" size="tiny" />
            <Text transform="uppercase" weight="semibold">
              {t('form.labels.loadMore')}
            </Text>
          </LoadMoreButton>
        )}
      </ListWrapper>

      <AnimatePresence>
        {isLoading && (
          <ListLoader
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.2 }}
          >
            <Spinner size="small" />
          </ListLoader>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(List);
